import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export default function CustomerCombobox({
  customers = [],
  value = '',
  onChange,
  placeholder = 'Select customer...',
  searchPlaceholder = 'Search customers...',
  emptyText = 'No customer found.',
  disabled = false,
  className,
  testId,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const searchRef = useRef(null);
  const listboxId = useRef(`customer-combobox-list-${Math.random().toString(36).slice(2)}`).current;

  const selectedCustomer = useMemo(
    () => customers.find((c) => String(c?.id || '') === String(value || '')) || null,
    [customers, value],
  );

  const filteredCustomers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter((c) => {
      const name = String(c?.name || '').toLowerCase();
      const phone = String(c?.phone || '');
      return name.includes(q) || phone.includes(q);
    });
  }, [query, customers]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      return;
    }
    const timer = window.setTimeout(() => searchRef.current?.focus(), 10);
    return () => window.clearTimeout(timer);
  }, [open]);

  const handleSelect = (customerId) => {
    if (onChange) onChange(customerId);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          disabled={disabled}
          data-testid={testId}
          className={cn(
            'relative h-10 w-full rounded-xl border border-gray-200 bg-white px-3 text-left text-sm text-stone-800 transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/40',
            'disabled:cursor-not-allowed disabled:opacity-60',
            className,
          )}
        >
          <span className={cn('truncate pr-6 block', !selectedCustomer && 'text-stone-400')}>
            {selectedCustomer
              ? `${selectedCustomer.name} (${Number(selectedCustomer.points || 0).toLocaleString()} pts)`
              : placeholder}
          </span>
          <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-[var(--radix-popover-trigger-width)] min-w-[280px] border border-gray-200 bg-white p-0 shadow-lg"
      >
        <div className="sticky top-0 z-10 border-b border-gray-100 bg-white px-3 py-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-stone-400" />
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              className="h-9 w-full rounded-lg border border-gray-200 bg-white pl-9 pr-2 text-sm text-stone-800 outline-none placeholder:text-stone-400 focus:border-teal-400"
            />
          </div>
        </div>

        <ul id={listboxId} role="listbox" className="max-h-64 overflow-y-auto py-1">
          {!filteredCustomers.length ? (
            <li className="px-3 py-8 text-center text-sm text-stone-500">{emptyText}</li>
          ) : (
            filteredCustomers.map((customer) => {
              const isSelected = String(customer.id || '') === String(value || '');
              return (
                <li key={customer.id} role="option" aria-selected={isSelected}>
                  <button
                    type="button"
                    onClick={() => handleSelect(customer.id)}
                    className={cn(
                      'flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left text-sm text-stone-800 transition-colors',
                      'hover:bg-teal-50 focus-visible:outline-none focus-visible:bg-teal-50',
                    )}
                  >
                    <span className="truncate">
                      {customer.name} ({Number(customer.points || 0).toLocaleString()} pts)
                    </span>
                    <Check className={cn('h-4 w-4 text-teal-600', isSelected ? 'opacity-100' : 'opacity-0')} />
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
